import React, { useRef, useEffect } from "react";

/**
 * SignaturePad — kanvas tanda tangan sederhana (tanpa dependency).
 * Props:
 *   value    : data URL PNG awal (opsional) untuk digambar ulang.
 *   onChange : (dataURL|"") dipanggil tiap selesai 1 goresan / saat dibersihkan.
 *   width/height : ukuran buffer kanvas (koordinat gambar).
 */
const SignaturePad = ({ value, onChange, width = 600, height = 180 }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);

  // Gambar ulang nilai awal sekali saat mount (jaga rasio, di tengah).
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    if (value) {
      const img = new Image();
      img.onload = () => {
        const s = Math.min(c.width / img.width, c.height / img.height);
        const dw = img.width * s;
        const dh = img.height * s;
        ctx.drawImage(img, (c.width - dw) / 2, (c.height - dh) / 2, dw, dh);
      };
      img.src = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Buang ruang kosong (crop ke kotak coretan) agar TTD tampil BESAR & jelas di
  // sel sempit. Tanpa ini, coretan kecil di tengah kanvas 600×180 ikut tersimpan
  // beserta ruang kosongnya → terlihat kecil saat ditampilkan.
  const trimmed = (c) => {
    const ctx = c.getContext("2d");
    const W = c.width;
    const H = c.height;
    const data = ctx.getImageData(0, 0, W, H).data;
    let minX = W;
    let minY = H;
    let maxX = -1;
    let maxY = -1;
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        if (data[(y * W + x) * 4 + 3] > 10) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return ""; // kanvas kosong
    const pad = 10;
    minX = Math.max(0, minX - pad);
    minY = Math.max(0, minY - pad);
    maxX = Math.min(W - 1, maxX + pad);
    maxY = Math.min(H - 1, maxY + pad);
    const w = maxX - minX + 1;
    const h = maxY - minY + 1;
    const out = document.createElement("canvas");
    out.width = w;
    out.height = h;
    out.getContext("2d").drawImage(c, minX, minY, w, h, 0, 0, w, h);
    return out.toDataURL("image/png");
  };

  const pos = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const t = e.touches && e.touches[0];
    const cx = (t ? t.clientX : e.clientX) - r.left;
    const cy = (t ? t.clientY : e.clientY) - r.top;
    return { x: cx * (c.width / r.width), y: cy * (c.height / r.height) };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const p = pos(e);
    ctx.strokeStyle = "#000000"; // hitam pekat (kontras tinggi)
    ctx.lineWidth = 9; // tebal/bold agar jelas saat ditampilkan kecil di sel
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const c = canvasRef.current;
    if (onChange) onChange(trimmed(c));
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    if (onChange) onChange("");
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{
          width: "100%",
          height,
          border: "1px dashed #94a3b8",
          borderRadius: 6,
          touchAction: "none",
          background: "#fff",
          cursor: "crosshair",
        }}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
      />
      <div className="d-flex justify-content-end mt-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={clear}
        >
          Bersihkan
        </button>
      </div>
    </div>
  );
};

export default SignaturePad;
